/**
 * Favorites Page - Display user's favorite Pokemon
 */
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Heart, Trash2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { PokemonCard, PokemonCardSkeleton } from '../components/pokedex/PokemonCard';
import { MegaPokemonCard } from '../components/pokedex/MegaPokemonCard';
import { RegionalPokemonCard } from '../components/pokedex/RegionalPokemonCard';
import { FormPokemonCard } from '../components/pokedex/FormPokemonCard';
import { getMegasAsPokemonList } from '../data/megaEvolutions';
import { getRegionalFormsAsPokemonList } from '../data/regionalForms';
import { getAlternateFormsAsPokemonList } from '../data/alternateForms';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function Favorites() {
  const { t, language, favorites, toggleFavorite } = useApp();
  const [loading, setLoading] = useState(true);

  // Special forms (megas 10001+, regionals 20001+, alternates 30001+) are stored
  // in favorites by their local id, which is NOT a valid PokeAPI id. Look each id
  // up so it renders with the correct card and links to the right detail route
  // instead of fetching a wrong/nonexistent Pokémon through the generic card.
  const specialForms = useMemo(() => {
    const map = new Map();
    getMegasAsPokemonList().forEach(p => map.set(p.id, p));
    getRegionalFormsAsPokemonList().forEach(p => map.set(p.id, p));
    getAlternateFormsAsPokemonList().forEach(p => map.set(p.id, p));
    return map;
  }, []);

  const renderFavoriteCard = (id, index) => {
    const special = specialForms.get(id);
    if (special?.isMega) {
      return <MegaPokemonCard key={`mega-${id}`} pokemon={special} index={index} />;
    }
    if (special?.isRegional) {
      return <RegionalPokemonCard key={`regional-${id}`} pokemon={special} index={index} />;
    }
    if (special?.isAlternate) {
      return <FormPokemonCard key={`form-${id}`} pokemon={special} index={index} />;
    }
    return <PokemonCard key={id} pokemon={{ id, name: `pokemon-${id}` }} index={index} />;
  };
  
  // Simulate initial load
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);
  
  const clearAllFavorites = () => {
    favorites.forEach(id => toggleFavorite(id));
  };
  
  return (
    <div className="min-h-screen" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl sm:text-4xl font-bold text-foreground flex items-center gap-3"
            >
              <Heart className="w-8 h-8 text-rose-500 fill-rose-500" />
              {t('favorites')}
            </motion.h1>
            <p className="text-muted-foreground mt-1">
              {favorites.length} Pokémon
            </p>
          </div>
          
          {favorites.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="clear-favorites-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  {language === 'es' ? 'Borrar todos' : 'Clear all'}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {language === 'es'
                      ? '¿Eliminar todos los favoritos?' 
                      : 'Remove all favorites?'
                    }
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {language === 'es'
                      ? 'Esta acción no se puede deshacer. Se eliminarán todos los Pokémon de tu lista de favoritos.'
                      : 'This action cannot be undone. All Pokémon will be removed from your favorites list.'
                    }
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{language === 'es' ? 'Cancelar' : 'Cancel'}</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={clearAllFavorites}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {language === 'es' ? 'Eliminar' : 'Remove'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <PokemonCardSkeleton key={i} />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-xl text-muted-foreground">{t('noFavorites')}</p>
            <p className="text-sm text-muted-foreground/70 mt-2">
              {language === 'es'
                ? 'Haz clic en el corazón de un Pokémon para añadirlo'
                : 'Click the heart on a Pokémon to add it'
              }
            </p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {favorites.map((id, index) => renderFavoriteCard(id, index))}
          </div>
        )}
      </div>
    </div>
  );
}
